import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import "./styleSheets/imageGrid.css";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

const DEFAULT_RATIO = 3 / 4;

const ImageCarousel = ({ slides }) => {
  const [current, setCurrent] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [loadedImages, setLoadedImages] = useState({});

  useEffect(() => {
    const updateMobileState = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    updateMobileState();
    window.addEventListener("resize", updateMobileState);
    return () => window.removeEventListener("resize", updateMobileState);
  }, []);

  useEffect(() => {
    if (!Array.isArray(slides) || slides.length === 0) {
      setLoadedImages({});
      return;
    }

    const nextLoadedState = {};
    slides.forEach((src) => {
      nextLoadedState[src] = false;
    });
    setLoadedImages(nextLoadedState);
  }, [slides]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        document.body.classList.remove("no-scroll");
      }
      if (event.key === "ArrowLeft") {
        setCurrent((active) => (active === 0 ? slides.length - 1 : active - 1));
      }
      if (event.key === "ArrowRight") {
        setCurrent((active) => (active === slides.length - 1 ? 0 : active + 1));
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, slides.length]);

  useEffect(() => {
    return () => {
      document.body.classList.remove("no-scroll");
    };
  }, []);

  const openModal = (index) => {
    document.body.classList.add("no-scroll");
    setCurrent(index);
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    document.body.classList.remove("no-scroll");
  };

  const navigateSlides = (direction) => {
    setCurrent((current) =>
      direction === "prev"
        ? current === 0
          ? slides.length - 1
          : current - 1
        : current === slides.length - 1
        ? 0
        : current + 1
    );
  };

  if (!Array.isArray(slides) || slides.length <= 0) {
    return null;
  }

  const eagerCount = isMobile ? 2 : 6;

  return (
    <>
      <div className="gallery-shell">
        <div className="grid">
          {slides.map((src, index) => (
            <button
              key={src}
              type="button"
              className={`grid-card ${loadedImages[src] ? "is-loaded" : "is-loading"}`}
              style={{ aspectRatio: DEFAULT_RATIO }}
              onClick={() => openModal(index)}
            >
              <div className="grid-item-skeleton" aria-hidden="true" />
              <img
                src={src}
                alt={`Slide ${index + 1}`}
                className="grid-item-image"
                loading={index < eagerCount ? "eager" : "lazy"}
                fetchPriority={index < eagerCount ? "high" : "low"}
                decoding="async"
                onLoad={() =>
                  setLoadedImages((prev) => ({
                    ...prev,
                    [src]: true,
                  }))
                }
              />
            </button>
          ))}
        </div>
      </div>

      {isOpen &&
        createPortal(
          <div className="modal-overlay" onClick={closeModal}>
            <div className="slider" onClick={(e) => e.stopPropagation()}>
              <img
                src={slides[current]}
                alt={`Slide ${current}`}
                className="image"
                loading="lazy"
              />
              <div className="navigation-buttons">
                <div
                  className="hover-area hover-left"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigateSlides("prev");
                  }}
                >
                  <FaChevronLeft />
                </div>
                <div
                  className="hover-area hover-right"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigateSlides("next");
                  }}
                >
                  <FaChevronRight />
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default ImageCarousel;
